import { useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { CampoNumero, Resultado, Dato, Rejilla, mxn } from "../components/campos.jsx";
import { presupuestarObra } from "../lib/calc.js";
import { PARTIDAS_OBRA, OBRA_REVISADO } from "../data/obra.js";

const inicial = PARTIDAS_OBRA.map((p) => ({ ...p, activa: true }));

export default function Obra() {
  const [metros, setMetros] = useState(100);
  const [partidas, setPartidas] = useState(inicial);
  const [desperdicio, setDesperdicio] = useState(5);
  const [indirectos, setIndirectos] = useState(10);
  const [utilidad, setUtilidad] = useState(10);
  const [iva, setIva] = useState(16);

  const r = useMemo(
    () =>
      presupuestarObra({
        metros,
        partidas,
        desperdicioPct: desperdicio,
        indirectosPct: indirectos,
        utilidadPct: utilidad,
        ivaPct: iva,
      }),
    [metros, partidas, desperdicio, indirectos, utilidad, iva],
  );

  const cambiar = (id, campo, valor) =>
    setPartidas((ps) => ps.map((p) => (p.id === id ? { ...p, [campo]: valor } : p)));

  const agregar = () =>
    setPartidas((ps) => [
      ...ps,
      { id: `x${Date.now()}`, nombre: "Partida adicional", unidad: "pza", cantidad: 1, precio: 0, activa: true },
    ]);

  return (
    <div className="space-y-6">
      <Rejilla>
        <div className="space-y-4">
          <CampoNumero label="Superficie de construcción" valor={metros} onChange={setMetros} sufijo="m²" min={0} />
          <CampoNumero label="Desperdicio y mermas" valor={desperdicio} onChange={setDesperdicio} sufijo="%" min={0} max={100} />
          <CampoNumero label="Indirectos (administración, obra)" valor={indirectos} onChange={setIndirectos} sufijo="%" min={0} max={100} />
          <CampoNumero label="Utilidad" valor={utilidad} onChange={setUtilidad} sufijo="%" min={0} max={100} />
          <CampoNumero label="IVA" valor={iva} onChange={setIva} sufijo="%" min={0} max={100} />
        </div>

        <div className="order-first space-y-3 lg:order-last lg:col-span-2">
          <Resultado
            label="Costo total de la obra"
            valor={mxn(r.total)}
            nota={`${mxn(r.porM2, 0)} por m² con IVA · ${metros} m²`}
          />
          <div className="tarjeta">
            <Dato label="Costo directo (partidas)" valor={mxn(r.directo)} />
            <Dato label={`Desperdicio (${desperdicio}%)`} valor={mxn(r.desperdicio)} />
            <Dato label={`Indirectos (${indirectos}%)`} valor={mxn(r.indirectos)} />
            <Dato label={`Utilidad (${utilidad}%)`} valor={mxn(r.utilidad)} />
            <Dato label="Subtotal" valor={mxn(r.subtotal)} />
            <Dato label={`IVA (${iva}%)`} valor={mxn(r.iva)} />
            <Dato label="Total" valor={mxn(r.total)} destacar />
          </div>
        </div>
      </Rejilla>

      <section aria-label="Partidas y precios unitarios" className="space-y-3">
        <h2 className="text-lg font-bold">Partidas y precios unitarios</h2>
        <p className="texto-3 text-xs">
          Precios de referencia ({OBRA_REVISADO}), editables. Cámbialos por los de tus cotizaciones. La cantidad
          por m² se multiplica por la superficie.
        </p>

        {partidas.map((p, i) => {
          const linea = r.lineas[i];
          const fija = p.ratio == null;
          return (
            <div key={p.id} className={`tarjeta space-y-3 p-4 ${p.activa ? "" : "opacity-60"}`}>
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={p.activa}
                  onChange={(e) => cambiar(p.id, "activa", e.target.checked)}
                  aria-label={`Incluir ${p.nombre}`}
                  className="mt-0.5 h-6 w-6 accent-[color:var(--acento)]"
                />
                {fija ? (
                  <input
                    className="campo flex-1"
                    value={p.nombre}
                    onChange={(e) => cambiar(p.id, "nombre", e.target.value)}
                    aria-label="Nombre de la partida"
                  />
                ) : (
                  <span className="flex-1 text-sm font-medium">{p.nombre}</span>
                )}
                {fija && (
                  <button
                    type="button"
                    onClick={() => setPartidas((ps) => ps.filter((x) => x.id !== p.id))}
                    aria-label={`Quitar ${p.nombre}`}
                    className="texto-3 p-2.5 -m-1.5"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <CampoNumero
                  label={fija ? `Cantidad (${p.unidad})` : `${p.unidad} por m²`}
                  valor={fija ? p.cantidad : p.ratio}
                  onChange={(v) => cambiar(p.id, fija ? "cantidad" : "ratio", v)}
                  min={0}
                />
                <CampoNumero label={`Precio por ${p.unidad}`} valor={p.precio} onChange={(v) => cambiar(p.id, "precio", v)} prefijo="$" min={0} />
                <div>
                  <span className="etiqueta">Importe</span>
                  <p className="font-mono text-sm font-bold pt-2.5">{mxn(linea.importe, 0)}</p>
                  <span className="texto-3 text-2xs">{linea.cantidad.toFixed(1)} {p.unidad}</span>
                </div>
              </div>
            </div>
          );
        })}

        <button type="button" onClick={agregar} className="tarjeta inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold">
          <Plus className="h-4 w-4" aria-hidden="true" /> Agregar partida
        </button>
      </section>
    </div>
  );
}
